import React from "react";
import { getStockCompanyIcon } from "utils";
import { ArrowDownLossIcon, ArrowUpProfitIcon, laptop } from "assets/icons";
import {
  AssetCell,
  AssetLogo,
  AssetSymbol,
  AssetName,
  PriceCell,
  PerformanceCell,
  IndustryCell,
} from "./AllHoldingsModal.styles";

// The API caps enrichment at 200 symbols and nulls a row whenever an FMP batch
// fails, so price/performance/industry are routinely absent (PG-2758). isNaN
// coerces null to 0, so these need an explicit nullish check ahead of it --
// `== null` and not a falsy check, or a real $0.00 becomes a dash.
const NO_DATA = "—";

// Local formatters to avoid named export issues from utils
const formatCurrency = (value) => {
  if (value == null) return NO_DATA;
  if (isNaN(value)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value) => {
  if (value == null) return NO_DATA;
  if (isNaN(value)) return "0.00%";
  return `${Number(value).toFixed(2)}%`;
};

export const columns = [
  {
    id: "asset",
    dataField: "asset",
    text: "Asset",
    props: {
      cell: ({ row }) => {
        const data = row.original;
        return (
          <AssetCell>
            <AssetLogo src={getStockCompanyIcon(data.asset)} alt={data.asset} />
            <div>
              <AssetSymbol>{data.asset}</AssetSymbol>
              <AssetName>{data.company_name}</AssetName>
            </div>
          </AssetCell>
        );
      },
    },
  },
  {
    id: "share_price",
    dataField: "share_price",
    text: "Share Price",
    props: {
      cell: ({ getValue }) => (
        <PriceCell>{formatCurrency(getValue())}</PriceCell>
      ),
    },
  },
  {
    id: "performance",
    dataField: "performance_1y_percent",
    text: "Performance",
    props: {
      cell: ({ getValue }) => {
        const value = getValue();
        // No pill when the return is unknown -- `null >= 0` is true, so it
        // would otherwise show as a gain.
        if (value == null) return <PerformanceCell>{NO_DATA}</PerformanceCell>;
        const isPositive = value >= 0;
        return (
          <PerformanceCell>
            <div className={`performance-pill ${isPositive ? "gain" : "loss"}`}>
              {isPositive ? <ArrowUpProfitIcon /> : <ArrowDownLossIcon />}
              {formatPercent(value)}
            </div>
            <div className="performance-period">1 Year</div>
          </PerformanceCell>
        );
      },
    },
  },
  {
    id: "industry",
    dataField: "industry",
    text: "Industry",
    props: {
      cell: ({ getValue }) => {
        const industry = getValue();
        // Icon with no label reads as a broken cell.
        if (industry == null) return <IndustryCell>{NO_DATA}</IndustryCell>;
        return (
          <IndustryCell>
            <img src={laptop} alt="Industry" />
            {industry}
          </IndustryCell>
        );
      },
    },
  },
];
